import { type AssetType, type WeightedSecurity } from '@fruitful/types';
import { PieChart, type pieDataItem } from 'react-native-gifted-charts';
import { BodyLargeRegular, type ColorTokenKey, useThemeColors } from 'smoothie';

const ASSET_COLORS: Record<AssetType, string[]> = {
    equities: [
        '$green1100',
        '$green1000',
        '$green900',
        '$green800',
        '$green700',
        '$green600',
        '$green500',
        '$green400',
        '$green300',
        '$green200',
        '$green100',
        '$green100',
    ],
    fixedIncome: [
        '$dropletBlue1100',
        '$dropletBlue1000',
        '$dropletBlue900',
        '$dropletBlue800',
        '$dropletBlue700',
        '$dropletBlue600',
        '$dropletBlue500',
        '$dropletBlue400',
        '$dropletBlue300',
        '$dropletBlue200',
        '$dropletBlue100',
        '$dropletBlue100',
    ],
    cash: [
        '$marigold1000',
        '$marigold900',
        '$marigold800',
        '$marigold700',
        '$marigold600',
        '$marigold500',
        '$marigold400',
        '$marigold300',
        '$marigold200',
        '$marigold100',
        '$marigold100',
        '$marigold100',
    ],
    unassigned: [
        '$gray1100',
        '$gray1000',
        '$gray900',
        '$gray800',
        '$gray700',
        '$gray600',
        '$gray500',
        '$gray400',
        '$gray300',
        '$gray200',
        '$gray100',
        '$gray100',
    ],
};

export const ApexPortfolioChart = ({
    portfolio,
    availableLiquidityAmount,
}: {
    portfolio: {
        cash: WeightedSecurity[];
        equities: WeightedSecurity[];
        fixedIncome: WeightedSecurity[];
        unassigned: WeightedSecurity[];
    };
    availableLiquidityAmount: string;
}) => {
    const { getColor } = useThemeColors();

    const { unassigned, cash, fixedIncome, equities } = portfolio;
    const sortedAssets = (portfolio: WeightedSecurity[]) => {
        return portfolio.sort((a, b) => {
            return b.weight - a.weight;
        });
    };

    const equityAssets = sortedAssets(equities);
    const fixedIncomeAssets = sortedAssets(fixedIncome);
    const cashAssets = sortedAssets(cash);
    const unassignedAssets = sortedAssets(unassigned);

    const pieData = (a: WeightedSecurity[], type: AssetType) => {
        return a.map((v, index) => {
            const color: ColorTokenKey = (ASSET_COLORS[type][index] ??
                '$gray500') as ColorTokenKey;
            return {
                value: v.weight,
                color: getColor(color),
            };
        });
    };

    const equityData: pieDataItem[] = pieData(equityAssets, 'equities');
    const fixedIncomeData: pieDataItem[] = pieData(
        fixedIncomeAssets,
        'fixedIncome',
    );
    const cashData: pieDataItem[] = pieData(cashAssets, 'cash');
    const unassignedData: pieDataItem[] = pieData(
        unassignedAssets,
        'unassigned',
    );

    const data = [
        ...equityData,
        ...fixedIncomeData,
        ...cashData,
        ...unassignedData,
    ];

    return (
        <PieChart
            donut
            data={data}
            radius={175}
            innerRadius={125}
            centerLabelComponent={() => {
                return (
                    <BodyLargeRegular>
                        {availableLiquidityAmount}
                    </BodyLargeRegular>
                );
            }}
            strokeColor={getColor('$white')}
            strokeWidth={4}
        />
    );
};
